import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import type { Observable } from 'rxjs';
import { switchMap, tap } from 'rxjs';

import { AuthStorage } from './auth-storage';
import { OPS_ROLES } from './role-tiers';
import { ToastService } from '../toast/toast.service';
import { environment } from '../../../environments/environment';
import type { ChangeOwnPasswordRequest, LoginRequest, LoginResponse, Role, UserIdentity } from '../../models/auth.model';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly toast = inject(ToastService);

  private readonly _token = signal<string | null>(AuthStorage.readToken());
  private readonly _user = signal<UserIdentity | null>(null);

  readonly token = this._token.asReadonly();
  readonly user = this._user.asReadonly();
  readonly isAuthenticated = computed(() => this._token() !== null);
  readonly roles = computed(() => this._user()?.roles ?? []);

  /**
   * The session token once the identity is loaded and holds an OPS role; null otherwise. For the
   * session-wide OPS-tier reads (alert stream, bypass status): a METRICS_VIEWER-only session, or
   * one whose roles are not known yet, never calls them (CS answers 403).
   */
  readonly opsSessionToken = computed(() => {
    const token = this._token();
    return token !== null && OPS_ROLES.some((role) => this.hasRole(role)) ? token : null;
  });

  login(credentials: LoginRequest): Observable<UserIdentity> {
    return this.http.post<LoginResponse>(`${environment.apiUrl}/auth/login`, credentials).pipe(
      tap((response) => {
        AuthStorage.writeToken(response.token, response.expiresIn);
        this._token.set(response.token);
      }),
      switchMap(() => this.loadMe()),
    );
  }

  loadMe(): Observable<UserIdentity> {
    return this.http
      .get<UserIdentity>(`${environment.apiUrl}/admin/me`)
      .pipe(tap((identity) => this._user.set(identity)));
  }

  changePassword(body: ChangeOwnPasswordRequest): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/admin/me/password`, body);
  }

  revokeAndLogout(): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/admin/me/logout`, {}).pipe(
      tap(() => this.logout()),
    );
  }

  logout(): void {
    AuthStorage.clear();
    this._token.set(null);
    this._user.set(null);
    // Los toasts son estado de la sesión: no deben quedar visibles sobre el login
    // ni filtrarse a la sesión de otro usuario en la misma pestaña.
    this.toast.clear();
  }

  hasRole(role: Role): boolean {
    return this.roles().includes(`ROLE_${role}`);
  }
}
